import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import './subscribe.css';
import './settings.css';
import './school-join.css';

/* Tick icon mirrors the one used on Settings/Subscribe so the feature
 * list matches the rest of the billing pages visually. */
const TickIcon = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="5 12 10 17 19 7" />
  </svg>
);

const TIERS = [
  {
    id: 'madrasa',
    name: 'DeenLit Madrasa',
    blurb: 'School-day access for small schools and co-ops.',
    monthly: 24.99,
    yearly: 199,
    students: 'Up to 300 students',
    hours: '7am – 5pm',
    features: ['School-hours access (7am–5pm)', 'Up to 300 student profiles', 'Cancel any time'],
  },
  {
    id: 'academy',
    name: 'DeenLit Academy',
    blurb: 'Round-the-clock access for established schools.',
    monthly: 59.99,
    yearly: 499,
    students: 'Up to 300 students',
    hours: '24 / 7',
    badge: 'Best value',
    features: ['24/7 access — read anywhere, anytime', 'Up to 300 student profiles', 'Cancel any time'],
  },
  {
    id: 'institute',
    name: 'DeenLit Institute',
    blurb: 'For larger schools with full year groups.',
    monthly: 79.99,
    yearly: 699,
    students: 'Up to 600 students',
    hours: '24 / 7',
    features: ['24/7 access', 'Up to 600 student profiles', 'Onboarding call included'],
  },
  {
    id: 'network',
    name: 'DeenLit Network',
    blurb: 'For multi-campus schools and academy chains.',
    custom: true,
    students: '600+ students',
    hours: '24 / 7',
    features: ['24/7 access', 'Unlimited student profiles'],
  },
];

export default function SchoolJoin() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const checkoutResult = params.get('checkout');
  const checkoutSchoolId = params.get('schoolId');
  const checkoutReference = params.get('reference');

  const [billing, setBilling] = useState('monthly');
  const [selectedTier, setSelectedTier] = useState('academy');
  const [form, setForm] = useState({ name: '', country: '', contactName: '', contactEmail: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const [paid, setPaid] = useState(null);
  useEffect(() => {
    if (checkoutResult !== 'success' || !checkoutSchoolId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/schools/checkout/result', { params: { schoolId: checkoutSchoolId, reference: checkoutReference } });
        if (!cancelled) setPaid(data);
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [checkoutResult, checkoutSchoolId, checkoutReference]);

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim()) return setErr('School name is required.');
    if (!form.contactEmail.trim()) return setErr('Contact email is required.');

    setSubmitting(true);
    try {
      const { data } = await axios.post('/api/schools/checkout', {
        name: form.name.trim(),
        country: form.country.trim(),
        contactName: form.contactName.trim(),
        contactEmail: form.contactEmail.trim(),
        notes: form.notes.trim(),
        tier: selectedTier,
        billing,
      });
      if (data?.url) { window.location.href = data.url; return; }
      setErr('Stripe did not return a checkout URL.');
    } catch (e2) {
      setErr(e2?.response?.data?.message || 'Could not start checkout. Try again.');
    } finally { setSubmitting(false); }
  };

  /* ── Post-Stripe success/cancel screens ─────────────────────────── */
  if (checkoutResult === 'success') {
    return (
      <div className="sub-page">
        <div className="sub-shell sub-shell--wide">
          <Link to="/school-portal" className="sub-back">← School portal</Link>
          <header className="sub-hero">
            <div className="sub-eyebrow">Card saved — awaiting approval</div>
            <h1 className="sub-title">Thanks {paid?.name || ''} — we'll review and approve.</h1>
            <p className="sub-blurb">
              Your card is securely saved with Stripe. <strong>No charge has been made yet.</strong>
            </p>
            <p className="sub-blurb">
              <strong>You'll get an email at your registered contact address</strong> the moment your school is approved — that's when billing starts and your school code goes live.
            </p>
            {paid?.code && (
              <p className="sub-blurb">
                Reference code: <code style={{ color: '#f0c560' }}>{paid.code}</code> (not usable yet).
              </p>
            )}
            <div style={{ marginTop: 18 }}>
              <Link to="/" className="sub-cta sub-cta-primary" style={{ textDecoration: 'none' }}>Back to library</Link>
            </div>
          </header>
        </div>
      </div>
    );
  }
  if (checkoutResult === 'cancelled') {
    return (
      <div className="sub-page">
        <div className="sub-shell sub-shell--wide">
          <Link to="/school-portal" className="sub-back">← School portal</Link>
          <header className="sub-hero">
            <div className="sub-eyebrow">Checkout cancelled</div>
            <h1 className="sub-title">No charge made.</h1>
            <p className="sub-blurb">Pick the plan again whenever you're ready.</p>
            <div style={{ marginTop: 18 }}>
              <Link to="/school-join" className="sub-cta sub-cta-primary" style={{ textDecoration: 'none' }}>Start over</Link>
            </div>
          </header>
        </div>
      </div>
    );
  }

  /* ── Plan picker + register form ────────────────────────────────── */
  const selected = TIERS.find((t) => t.id === selectedTier);
  const ctaPrice = selected ? `£${selected[billing]}/${billing === 'monthly' ? 'mo' : 'yr'}` : '';

  return (
    <div className="sub-page">
      <div className="sub-shell sub-shell--wide">
        <Link to="/school-portal" className="sub-back">← School portal</Link>

        <header className="sub-hero">
          <div className="sub-eyebrow">Join DeenLit</div>
          <h1 className="sub-title">Bring DeenLit to your school</h1>
          <p className="sub-blurb">
            Pick the plan that fits your size — admins approve each school personally before billing starts.
          </p>
        </header>

        <div className="sj-billing" data-active={billing} style={{ margin: '0 auto 8px' }}>
          <button
            type="button"
            className={`sj-billing-btn ${billing === 'monthly' ? 'is-active' : ''}`}
            onClick={() => setBilling('monthly')}
          >
            Monthly
          </button>
          <button
            type="button"
            className={`sj-billing-btn ${billing === 'yearly' ? 'is-active' : ''}`}
            onClick={() => setBilling('yearly')}
          >
            Yearly <span className="sj-billing-save">save ~30%</span>
          </button>
        </div>

        <div className="sub-tier-grid">
          {TIERS.map((tier) => {
            const isPopular = tier.id === 'academy';
            const isCurrent = tier.id === selectedTier;
            const isCustom = !!tier.custom;
            const price = isCustom ? 'Custom' : `£${tier[billing]}`;
            const period = isCustom ? '' : (billing === 'monthly' ? '/ mo' : '/ yr');
            return (
              <div
                key={tier.id}
                className={`sub-tier${isPopular ? ' is-popular' : ''}${isCurrent ? ' is-current' : ''}`}
                onClick={() => setSelectedTier(tier.id)}
                style={{ cursor: 'pointer' }}
              >
                {isCurrent ? (
                  <div className="sub-tier-badge sub-tier-badge--current">Selected</div>
                ) : tier.badge ? (
                  <div className="sub-tier-badge">{tier.badge}</div>
                ) : null}
                <div className="sub-tier-name">{tier.name}</div>
                <div className="sub-tier-price">
                  <span className="sub-tier-price-amount">{price}</span>
                  <span className="sub-tier-price-period">{period}</span>
                </div>
                <div className="sub-tier-profiles">{tier.students}</div>
                <ul className="sub-tier-features">
                  {tier.features.map((f) => (
                    <li key={f}>
                      <span className="sub-features-check"><TickIcon /></span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className={`sub-cta ${isCurrent ? 'sub-cta-primary' : 'sub-cta-secondary'}`}
                  onClick={(e) => { e.stopPropagation(); setSelectedTier(tier.id); }}
                >
                  {isCurrent ? 'Selected' : 'Choose'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="sub-card" style={{ width: '100%', margin: '20px auto 0', padding: '24px 26px 22px' }}>
          <h2 style={{ margin: '0 0 14px', fontFamily: "'Cormorant Garamond','Georgia',serif", fontSize: '1.6rem', color: '#fff8e6' }}>
            Tell us about your school
          </h2>
          <form className="sj-form" onSubmit={submit} noValidate style={{ background: 'transparent', border: 0, padding: 0 }}>
            <div className="sj-form-grid">
              <label className="auth-field">
                <span>School name *</span>
                <input type="text" name="name" value={form.name} onChange={handleField} placeholder="e.g. Al-Madinah Primary" required />
              </label>
              <label className="auth-field">
                <span>Country</span>
                <input type="text" name="country" value={form.country} onChange={handleField} placeholder="United Kingdom" />
              </label>
              <label className="auth-field">
                <span>Your name</span>
                <input type="text" name="contactName" value={form.contactName} onChange={handleField} placeholder="Head teacher / contact" />
              </label>
              <label className="auth-field">
                <span>Contact email *</span>
                <input type="email" name="contactEmail" value={form.contactEmail} onChange={handleField} placeholder="you@school.com" required />
              </label>
              <label className="auth-field sj-form-wide">
                <span>Anything we should know?</span>
                <textarea name="notes" value={form.notes} onChange={handleField} rows={3} placeholder="Approximate student count, year groups, timeline…" />
              </label>
            </div>

            {err && (
              <div className="sub-notice is-error" style={{ marginTop: 12 }}>
                <div className="sub-notice-row">
                  <span className="sub-notice-icon">!</span>
                  <div>{err}</div>
                </div>
              </div>
            )}

            <button type="submit" className="sub-cta sub-cta-primary" style={{ width: '100%', marginTop: 14 }} disabled={submitting}>
              {submitting ? 'Redirecting to Stripe…' : `Continue to payment — ${selected?.name} (${ctaPrice})`}
            </button>
            <p className="sub-fineprint" style={{ marginTop: 10 }}>
              Secure payment by Stripe. Card saved at checkout — no charge until an admin approves.{' '}
              Already have a school code? <Link to="/login?role=staff" className="auth-link">Staff sign in &rarr;</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
